import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ProductViewModel } from '~/lib/data-contract';
import { PriceGroup, SizeSelectorLegacy, Text } from '~/shared/components';
import { ProductImage } from '~/shared/components/_ProductImage';
import { queriesIsolated } from '~/theme';
import {
    StyledProductLine,
    StyledProductReceiptHeader,
    StyledProductImageWrapper,
    StyledProductInfo,
    StyledSizeSelectorWrapper,
} from './styled';
import { ReserveStockCount } from '../ReserveStockCount/ReserveStockCount';
import { useStoresWithStock } from '~/features/stores';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '~/shared/utils';
import { useAccount } from '$features/club';

type ReserveSectionProductProps = {
    product: ProductViewModel;
    isReceipt: boolean;
};

export const ReserveSectionProduct = memo(({ product, isReceipt }: ReserveSectionProductProps) => {
    const { translate } = useTranslation();
    const [shouldShakeSizeSelector, setShouldShakeSizeSelector] = useState(false);
    const { images, name, priceDetails, colors, sizes, productId, productColorId } = product;

    const {
        watch,
        setValue,
        formState: { submitCount },
    } = useFormContext();

    const activeVariantId = watch('variantId');

    const { account } = useAccount();
    const {
        data: { stores },
    } = useStoresWithStock({
        searchParams: {
            Radius: 10_000_000,
            ZipCode: '0',
        },
        searchTerm: '',
        slugs: {
            productColorId,
            variantId: activeVariantId || undefined,
        },
    });

    const handleOnSelect = useCallback(
        (variantId: string) => {
            // Set the variantId value on the form
            setValue('variantId', variantId);
        },
        [setValue]
    );

    useEffect(() => {
        if (submitCount && !activeVariantId) {
            setShouldShakeSizeSelector(true);
        }
    }, [submitCount, activeVariantId, setShouldShakeSizeSelector]);

    const activeColor = useMemo(
        () => colors?.find((color) => color.productColorId === productColorId)?.color,
        [colors, productColorId]
    );

    const selectedVariant = useMemo(
        () => sizes?.find((size) => size.variantId === activeVariantId),
        [sizes, activeVariantId]
    );

    const imageSize = {
        xs: 62,
        sm: 96,
        md: isReceipt ? 96 : 156,
    };

    return (
        <div>
            {isReceipt && (
                <StyledProductReceiptHeader>
                    <Text variant="display4" as="h2">
                        {translate('reservation.receipt.headline')}
                    </Text>
                    <Text variant="bodySm">{translate('reservation.receipt.bodyText')}</Text>
                </StyledProductReceiptHeader>
            )}
            <StyledProductLine isCentered={isReceipt}>
                <StyledProductImageWrapper imageSize={imageSize}>
                    <ProductImage
                        image={images?.[0]}
                        skeletonShade="light"
                        sizes={`${queriesIsolated.xs} ${imageSize.xs}px, ${queriesIsolated.sm} ${imageSize.sm}px, ${imageSize.md}px`}
                        alt={`${name} ${productId}`}
                        style={{
                            objectFit: 'contain',
                            aspectRatio: '2/3',
                            width: '100%',
                            height: '100%',
                        }}
                    />
                </StyledProductImageWrapper>
                <StyledProductInfo>
                    <div>
                        <Text as="h2" variant="display4">
                            {name}
                        </Text>
                        <Text as="span" variant="caption">
                            {activeColor}
                        </Text>
                    </div>
                    <PriceGroup {...priceDetails} orientation="vertical-top" />
                </StyledProductInfo>
                {isReceipt ? null : (
                    <StyledSizeSelectorWrapper>
                        <SizeSelectorLegacy
                            isAnimating={shouldShakeSizeSelector}
                            name="reserve-sizes"
                            sizes={sizes}
                            activeSizeId={activeVariantId}
                            showErrorMessage={submitCount > 0 && !activeVariantId}
                            onAnimationEnd={() => setShouldShakeSizeSelector(false)}
                            onSelect={handleOnSelect}
                            stockFrom="store"
                        />
                        {activeVariantId && (
                            <ReserveStockCount
                                stores={selectedVariant?.inStockInStore ? stores : undefined}
                                account={selectedVariant?.inStockInStore ? account : undefined}
                            />
                        )}
                    </StyledSizeSelectorWrapper>
                )}
            </StyledProductLine>
        </div>
    );
});
